import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import GUI from "lil-gui";

/**
 * Base
 */
// Mobile detection - Enhanced version
const isMobile = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i;

  return (
    window.innerWidth <= 768 ||
    mobileRegex.test(userAgent) ||
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0
  );
};

// Handle GUI responsive behavior - Enhanced version
const handleGUIResponsive = () => {
  const isCurrentlyMobile = isMobile();

  if (isCurrentlyMobile) {
    // Hide GUI on mobile by default
    gui.close();

    // Adjust GUI size for mobile
    const width = window.innerWidth <= 480 ? "280px" : "300px";
    gui.domElement.style.width = width;

    // Position GUI in center when opened on mobile
    gui.domElement.style.position = "fixed";
    gui.domElement.style.top = "50%";
    gui.domElement.style.left = "50%";
    gui.domElement.style.transform = "translate(-50%, -50%)";
    gui.domElement.style.zIndex = "2000";
    gui.domElement.style.maxHeight = window.innerWidth <= 480 ? "70vh" : "80vh";
    gui.domElement.style.overflowY = "auto";
    gui.domElement.style.borderRadius = "8px";
    gui.domElement.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.5)";

    // Add mobile-specific class for additional styling
    gui.domElement.classList.add("mobile-gui");

    console.log("Mobile mode: GUI hidden, use settings button to access");
  } else {
    // Desktop behavior
    gui.domElement.style.width = "360px";
    gui.domElement.style.position = "";
    gui.domElement.style.top = "";
    gui.domElement.style.left = "";
    gui.domElement.style.transform = "";
    gui.domElement.style.maxHeight = "";
    gui.domElement.style.overflowY = "";
    gui.domElement.style.borderRadius = "";
    gui.domElement.style.boxShadow = "";

    gui.domElement.classList.remove("mobile-gui");

    console.log("Desktop mode: GUI visible");
  }
};

// Debug - GUI with mobile-responsive settings
const gui = new GUI({
  width: 360,
  closed: isMobile(), // Hide controls initially on mobile
  title: "🌌 Galaxy Controls",
});

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x000010); // Dark blue space background

/**
 * Lighting for planets
 */
const ambientLight = new THREE.AmbientLight(0x404040, 0.3); // Soft ambient light
scene.add(ambientLight);

const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
directionalLight.position.set(5, 5, 5);
scene.add(directionalLight);

// Add point lights for dramatic effect
const pointLight1 = new THREE.PointLight(0xff6600, 0.5, 10);
pointLight1.position.set(2, 0, 2);
scene.add(pointLight1);

const pointLight2 = new THREE.PointLight(0x0066ff, 0.3, 8);
pointLight2.position.set(-3, 2, -1);
scene.add(pointLight2);

/**
 * Audio System
 */
const audioContext = new (window.AudioContext || window.webkitAudioContext)();
let audioBuffer = null;
let audioSource = null;
let gainNode = null;
let isPlaying = false;

// Create procedural ambient space music
const createAmbientMusic = () => {
  const sampleRate = audioContext.sampleRate;
  const duration = 60; // 60 seconds loop
  const bufferLength = sampleRate * duration;

  audioBuffer = audioContext.createBuffer(2, bufferLength, sampleRate);

  for (let channel = 0; channel < 2; channel++) {
    const channelData = audioBuffer.getChannelData(channel);

    for (let i = 0; i < bufferLength; i++) {
      const time = i / sampleRate;

      // Create multiple sine waves for ambient pad sounds
      const freq1 = 55 + Math.sin(time * 0.1) * 5; // Low bass drone
      const freq2 = 110 + Math.sin(time * 0.15) * 10; // Mid drone
      const freq3 = 220 + Math.sin(time * 0.08) * 15; // Higher pad

      const wave1 = Math.sin(2 * Math.PI * freq1 * time) * 0.1;
      const wave2 = Math.sin(2 * Math.PI * freq2 * time) * 0.08;
      const wave3 = Math.sin(2 * Math.PI * freq3 * time) * 0.05;

      // Add some filtered noise for texture
      const noise = (Math.random() - 0.5) * 0.02;

      // Combine waves with slow amplitude modulation
      const envelope = 0.5 + 0.3 * Math.sin(time * 0.2);
      channelData[i] = (wave1 + wave2 + wave3 + noise) * envelope;
    }
  }
};

const playAmbientMusic = () => {
  if (audioContext.state === "suspended") {
    audioContext.resume();
  }

  if (!isPlaying) {
    audioSource = audioContext.createBufferSource();
    gainNode = audioContext.createGain();

    audioSource.buffer = audioBuffer;
    audioSource.loop = true;
    gainNode.gain.value = 0.3; // Volume

    audioSource.connect(gainNode);
    gainNode.connect(audioContext.destination);

    audioSource.start();
    isPlaying = true;
  }
};

const stopAmbientMusic = () => {
  if (audioSource && isPlaying) {
    audioSource.stop();
    isPlaying = false;
  }
};

// Initialize music
createAmbientMusic();

/**
 * Galaxy
 */
const paramaters = {};
paramaters.count = 300000; // Increased particle count for more density
paramaters.size = 0.009; // Smaller particles for finer detail
paramaters.radius = 3; // Larger galaxy radius
paramaters.branches = 6; // Classic spiral galaxy look
paramaters.spin = 3; // More pronounced spiral
paramaters.randomness = 0.6; // Less randomness for cleaner structure
paramaters.randomnessPower = 3; // Better distribution
paramaters.insideColor = "#ff9500"; // Warm orange center
paramaters.outsideColor = "#0066ff"; // Cool blue edges

// Planet parameters
paramaters.planetCount = 15;
paramaters.planetMinSize = 0.05;
paramaters.planetMaxSize = 0.3;

/**
 * Planet System
 */
const planetTypes = [
  {
    name: "Rocky",
    color: "#8B4513",
    roughness: 0.8,
    metalness: 0.2,
    emissive: "#000000",
  },
  {
    name: "Gas Giant",
    color: "#FFA500",
    roughness: 0.1,
    metalness: 0.0,
    emissive: "#221100",
  },
  {
    name: "Ice World",
    color: "#87CEEB",
    roughness: 0.3,
    metalness: 0.1,
    emissive: "#001122",
  },
  {
    name: "Desert",
    color: "#DAA520",
    roughness: 0.9,
    metalness: 0.0,
    emissive: "#110800",
  },
  {
    name: "Ocean",
    color: "#006994",
    roughness: 0.0,
    metalness: 0.8,
    emissive: "#000011",
  },
  {
    name: "Volcanic",
    color: "#FF4500",
    roughness: 0.7,
    metalness: 0.3,
    emissive: "#330000",
  },
  {
    name: "Forest",
    color: "#228B22",
    roughness: 0.6,
    metalness: 0.0,
    emissive: "#001100",
  },
  {
    name: "Crystal",
    color: "#9966CC",
    roughness: 0.0,
    metalness: 1.0,
    emissive: "#110022",
  },
  {
    name: "Lava",
    color: "#FF6600",
    roughness: 0.8,
    metalness: 0.0,
    emissive: "#FF2200",
  },
];

let planets = [];
let geometry = null;
let material = null;
let points = null;

const createPlanetTexture = (planetType) => {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");

  // Create base color
  ctx.fillStyle = planetType.color;
  ctx.fillRect(0, 0, 512, 512);

  // Add surface details
  for (let i = 0; i < 100; i++) {
    const x = Math.random() * 512;
    const y = Math.random() * 512;
    const radius = Math.random() * 20 + 5;

    ctx.globalAlpha = 0.3;
    ctx.fillStyle = `hsl(${Math.random() * 360}, 50%, ${
      Math.random() * 50 + 25
    }%)`;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalAlpha = 1;
  const texture = new THREE.CanvasTexture(canvas);
  return texture;
};

const generatePlanets = () => {
  // Clear existing planets
  planets.forEach((planet) => {
    scene.remove(planet.mesh);
    if (planet.atmosphere) scene.remove(planet.atmosphere);
    if (planet.trail) scene.remove(planet.trail);
    planet.geometry.dispose();
    planet.material.dispose();
    if (planet.atmosphereGeometry) planet.atmosphereGeometry.dispose();
    if (planet.atmosphereMaterial) planet.atmosphereMaterial.dispose();
  });
  planets = [];

  for (let i = 0; i < paramaters.planetCount; i++) {
    const planetType =
      planetTypes[Math.floor(Math.random() * planetTypes.length)];
    const size =
      paramaters.planetMinSize +
      Math.random() * (paramaters.planetMaxSize - paramaters.planetMinSize);

    // Planet geometry
    const geometry = new THREE.SphereGeometry(size, 32, 32);

    // Create procedural texture
    const texture = createPlanetTexture(planetType);

    // Planet material
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      roughness: planetType.roughness,
      metalness: planetType.metalness,
      emissive: new THREE.Color(planetType.emissive),
      emissiveIntensity: 0.2,
    });

    const mesh = new THREE.Mesh(geometry, material);

    // Create atmosphere effect
    const atmosphereGeometry = new THREE.SphereGeometry(size * 1.2, 32, 32);
    const atmosphereMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(planetType.color),
      transparent: true,
      opacity: 0.1,
      side: THREE.BackSide,
    });
    const atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);

    // Position planets at various distances from galaxy center
    const distance = 1 + Math.random() * 8;
    const angle = Math.random() * Math.PI * 2;
    const height = (Math.random() - 0.5) * 2;

    mesh.position.x = Math.cos(angle) * distance;
    mesh.position.z = Math.sin(angle) * distance;
    mesh.position.y = height;

    atmosphere.position.copy(mesh.position);

    // Create orbital trail
    const trailGeometry = new THREE.RingGeometry(
      distance - 0.02,
      distance + 0.02,
      64
    );
    const trailMaterial = new THREE.MeshBasicMaterial({
      color: 0x444444,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide,
    });
    const trail = new THREE.Mesh(trailGeometry, trailMaterial);
    trail.rotation.x = -Math.PI / 2;

    // Store planet data
    const planet = {
      mesh: mesh,
      geometry: geometry,
      material: material,
      atmosphere: atmosphere,
      atmosphereGeometry: atmosphereGeometry,
      atmosphereMaterial: atmosphereMaterial,
      trail: trail,
      type: planetType,
      distance: distance,
      angle: angle,
      speed: 0.1 / distance, // Closer planets orbit faster
      rotationSpeed: Math.random() * 0.02 + 0.005,
      size: size,
    };

    planets.push(planet);
    scene.add(mesh);
    scene.add(atmosphere);
    scene.add(trail);
  }
};

const generateGalaxy = () => {
  // Destroy old galaxy
  if (points !== null) {
    geometry.dispose();
    material.dispose();
    scene.remove(points);
  }

  /**
   * Geometry
   */
  geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(paramaters.count * 3);
  const colors = new Float32Array(paramaters.count * 3);
  const sizes = new Float32Array(paramaters.count); // Add size attribute

  const colorInside = new THREE.Color(paramaters.insideColor);
  const colorOutside = new THREE.Color(paramaters.outsideColor);

  for (let i = 0; i < paramaters.count; i++) {
    const i3 = i * 3;

    // Position
    const radius = Math.random() * paramaters.radius;
    const spinAngle = radius * paramaters.spin;
    const branchAngle =
      ((i % paramaters.branches) / paramaters.branches) * Math.PI * 2;

    const randomX =
      Math.pow(Math.random(), paramaters.randomnessPower) *
      (Math.random() < 0.5 ? 1 : -1) *
      paramaters.randomness;
    const randomY =
      Math.pow(Math.random(), paramaters.randomnessPower) *
      (Math.random() < 0.5 ? 1 : -1) *
      paramaters.randomness;
    const randomZ =
      Math.pow(Math.random(), paramaters.randomnessPower) *
      (Math.random() < 0.5 ? 1 : -1) *
      paramaters.randomness;

    positions[i3 + 0] =
      radius * Math.cos(branchAngle + spinAngle) * radius + randomX;
    positions[i3 + 1] = 0 + randomY;
    positions[i3 + 2] =
      radius * Math.sin(branchAngle + spinAngle) * radius + randomZ;

    // Color with enhanced gradients
    const mixedColor = colorInside.clone();

    // Create more complex color transitions
    const normalizedRadius = radius / paramaters.radius;

    // Add middle color for more vibrant transition
    const middleColor = new THREE.Color("#ff0080"); // Magenta middle

    if (normalizedRadius < 0.5) {
      // Inner to middle transition
      mixedColor.lerp(middleColor, normalizedRadius * 2);
    } else {
      // Middle to outer transition
      mixedColor.copy(middleColor);
      mixedColor.lerp(colorOutside, (normalizedRadius - 0.5) * 2);
    }

    // Add some brightness variation for sparkle effect
    const brightness = 0.7 + Math.random() * 0.3;
    mixedColor.multiplyScalar(brightness);

    colors[i3 + 0] = mixedColor.r;
    colors[i3 + 1] = mixedColor.g;
    colors[i3 + 2] = mixedColor.b;

    // Size variation - larger particles in center, smaller at edges
    const sizeVariation = Math.random() * 0.5 + 0.5; // Random variation 0.5-1.0
    const distanceFromCenter = radius / paramaters.radius;
    const centerBoost = (1 - distanceFromCenter) * 2 + 0.5; // Larger in center
    sizes[i] = sizeVariation * centerBoost;
  }
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1)); // Add size attribute

  /**
   * Material
   */
  material = new THREE.ShaderMaterial({
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexColors: true,
    uniforms: {
      uTime: { value: 0 },
      uSize: { value: paramaters.size },
    },
    vertexShader: `
      uniform float uTime;
      uniform float uSize;
      
      attribute float size;
      
      varying vec3 vColor;
      
      void main() {
        vec3 newPosition = position;
        
        // Add spiral motion - particles move along their orbital path
        float radius = length(vec2(position.x, position.z));
        float angle = atan(position.z, position.x);
        
        // Different rotation speeds based on distance (inner faster than outer)
        float rotationSpeed = (1.0 / (radius + 1.0)) * 0.3;
        angle += uTime * rotationSpeed;
        
        // Apply the spiral motion
        newPosition.x = cos(angle) * radius;
        newPosition.z = sin(angle) * radius;
        
        vec4 modelPosition = modelMatrix * vec4(newPosition, 1.0);
        vec4 viewPosition = viewMatrix * modelPosition;
        vec4 projectedPosition = projectionMatrix * viewPosition;
        
        gl_Position = projectedPosition;
        
        // Pulsing effect with size variation
        float pulsing = sin(uTime * 2.0 + position.x * 0.5) * 0.3 + 1.0;
        gl_PointSize = size * uSize * pulsing * 300.0;
        gl_PointSize *= (1.0 / -viewPosition.z);
        
        vColor = color;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      
      void main() {
        // Create circular particles with bright center and soft glowing edges
        float strength = distance(gl_PointCoord, vec2(0.5));
        strength = 1.0 - strength;
        
        // Create a bright core with outer glow
        float innerCore = pow(strength, 5.0);
        float outerGlow = pow(strength, 2.0);
        
        // Combine core and glow
        float finalStrength = innerCore + outerGlow * 0.3;
        
        // Apply color with enhanced brightness
        vec3 color = vColor * (1.0 + finalStrength * 2.0);
        
        gl_FragColor = vec4(color, finalStrength);
      }
    `,
  });

  /**
   * Points
   */
  points = new THREE.Points(geometry, material);
  scene.add(points);
};

// Auto-start music on first user interaction
canvas.addEventListener(
  "click",
  () => {
    if (!isPlaying) {
      playAmbientMusic();
      updateMusicStatus();
    }
  },
  { once: true }
);

/**
 * UI Functionality
 */
let currentTutorialStep = 1;
let tutorialVisible = true;

// DOM Elements
const loadingScreen = document.getElementById("loading-screen");
const progressBar = document.getElementById("progress-bar");
const infoToggle = document.getElementById("info-toggle");
const infoContent = document.getElementById("info-content");
const starCount = document.getElementById("star-count");
const planetCount = document.getElementById("planet-count");
const musicStatus = document.getElementById("music-status");
const settingsToggle = document.getElementById("settings-toggle");
const musicToggle = document.getElementById("music-toggle");
const fullscreenToggle = document.getElementById("fullscreen-toggle");
const resetView = document.getElementById("reset-view");
const screenshot = document.getElementById("screenshot");
const shareTwitter = document.getElementById("share-twitter");
const shareFacebook = document.getElementById("share-facebook");
const copyLink = document.getElementById("copy-link");
const tutorialModal = document.getElementById("tutorial-modal");
const tutorialClose = document.getElementById("tutorial-close");
const tutorialPrev = document.getElementById("tutorial-prev");
const tutorialNext = document.getElementById("tutorial-next");
const tutorialSkip = document.getElementById("tutorial-skip");

// Loading screen simulation
const simulateLoading = () => {
  let progress = 0;
  const interval = setInterval(() => {
    progress += Math.random() * 15;
    if (progress >= 100) {
      progress = 100;
      clearInterval(interval);
      setTimeout(() => {
        loadingScreen.classList.add("hidden");
        // Show tutorial on first visit
        if (localStorage.getItem("galaxyTutorialSeen") !== "true") {
          showTutorial();
        }
      }, 500);
    }
    progressBar.style.width = progress + "%";
  }, 200);
};

// Info panel toggle
const toggleInfo = () => {
  infoContent.classList.toggle("visible");
};

// Update statistics
const updateStats = () => {
  if (starCount) starCount.textContent = paramaters.count.toLocaleString();
  if (planetCount) planetCount.textContent = paramaters.planetCount;
};

// Update music status
const updateMusicStatus = () => {
  if (musicStatus) {
    musicStatus.textContent = isPlaying ? "🎵 Playing" : "🔇 Muted";
  }
};

// Settings toggle (for mobile-friendly GUI control)
const toggleSettings = () => {
  if (gui._closed) {
    gui.open();

    // Apply mobile positioning if on mobile device
    if (isMobile()) {
      setTimeout(() => {
        const width = window.innerWidth <= 480 ? "280px" : "300px";
        gui.domElement.style.width = width;
        gui.domElement.style.position = "fixed";
        gui.domElement.style.top = "50%";
        gui.domElement.style.left = "50%";
        gui.domElement.style.transform = "translate(-50%, -50%)";
        gui.domElement.style.zIndex = "2000";
        gui.domElement.style.maxHeight =
          window.innerWidth <= 480 ? "70vh" : "80vh";
        gui.domElement.style.overflowY = "auto";
        gui.domElement.style.borderRadius = "8px";
        gui.domElement.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.5)";
        gui.domElement.classList.add("mobile-gui");
      }, 50); // Small delay to ensure DOM is updated
    }

    showNotification("⚙️ Settings panel opened", "info");
  } else {
    gui.close();
    showNotification("⚙️ Settings panel closed", "info");
  }
};

// Music toggle
const toggleMusic = () => {
  if (isPlaying) {
    stopAmbientMusic();
  } else {
    playAmbientMusic();
  }
  updateMusicStatus();
};

// Fullscreen toggle
const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }
};

// Reset camera view
const resetCameraView = () => {
  camera.position.set(6, 4, 6);
  controls.reset();
};

// Take screenshot
const takeScreenshot = () => {
  try {
    // Ensure renderer is available and ready
    if (typeof renderer === "undefined" || !renderer || !renderer.domElement) {
      showNotification(
        "⏳ Renderer not ready. Please wait a moment...",
        "warning"
      );
      return;
    }

    // Get canvas element
    const canvas = renderer.domElement;

    // Check if canvas has content
    if (!canvas.width || !canvas.height) {
      showNotification("❌ Canvas not ready. Please try again.", "error");
      return;
    }

    // Show loading message
    showNotification("📸 Capturing screenshot...", "info");

    // Force multiple renders to ensure everything is properly drawn
    renderer.render(scene, camera);
    renderer.render(scene, camera);

    // Use requestAnimationFrame to ensure the render is complete
    requestAnimationFrame(() => {
      try {
        // Convert canvas to blob with high quality
        canvas.toBlob(
          (blob) => {
            if (blob && blob.size > 1000) {
              // Check if blob has actual content (>1KB)
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              const timestamp = new Date()
                .toISOString()
                .slice(0, 19)
                .replace(/[:-]/g, "");
              link.download = `epic-galaxy-${timestamp}.png`;
              link.href = url;
              link.style.display = "none";

              // Add to DOM, click, and remove
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              // Clean up the blob URL after a short delay
              setTimeout(() => URL.revokeObjectURL(url), 1000);

              // Show success message
              showNotification("✨ Galaxy screenshot saved!", "success");
            } else {
              // If blob is empty or too small, try fallback method
              console.warn("Blob is empty, trying fallback method");
              fallbackScreenshot(canvas);
            }
          },
          "image/png",
          1.0
        );
      } catch (innerError) {
        console.error("Canvas conversion error:", innerError);
        fallbackScreenshot(canvas);
      }
    });
  } catch (error) {
    console.error("Screenshot error:", error);
    showNotification("❌ Screenshot failed. Please try again.", "error");
  }
};

// Fallback screenshot method
const fallbackScreenshot = (canvas) => {
  try {
    const dataURL = canvas.toDataURL("image/png", 1.0);

    // Check if dataURL contains actual image data (not just empty canvas)
    if (
      dataURL ===
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
    ) {
      showNotification(
        "❌ Canvas appears to be empty. Try again in a moment.",
        "error"
      );
      return;
    }

    const link = document.createElement("a");
    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, "");
    link.download = `epic-galaxy-${timestamp}.png`;
    link.href = dataURL;
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showNotification("✨ Galaxy screenshot saved! (fallback)", "success");
  } catch (fallbackError) {
    console.error("Fallback screenshot error:", fallbackError);
    showNotification(
      "❌ Screenshot failed. Browser may not support this feature.",
      "error"
    );
  }
};

// Share functions
const shareOnTwitter = () => {
  try {
    const text = getShareMessage();
    const url = getShareableURL();
    const hashtags = "Galaxy,3D,WebGL,Space,ThreeJS,Interactive";
    const twitterURL = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text
    )}&url=${encodeURIComponent(url)}&hashtags=${encodeURIComponent(hashtags)}`;

    // Try to open popup with specific dimensions for Twitter
    const popup = window.open(
      twitterURL,
      "twitter-share",
      "width=550,height=420,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no"
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      // Popup blocked, fallback to new tab
      showNotification("🐦 Opening Twitter in new tab (popup blocked)", "info");
      window.open(twitterURL, "_blank", "noopener,noreferrer");
    } else {
      showNotification("🐦 Opening Twitter share window...", "success");

      // Check if popup was closed quickly (popup blocker)
      setTimeout(() => {
        if (popup.closed) {
          showNotification(
            "📋 Popup was blocked - opened in new tab instead",
            "info"
          );
        }
      }, 1000);
    }
  } catch (error) {
    console.error("Twitter share error:", error);
    showNotification(
      "❌ Failed to share on Twitter. Please try again.",
      "error"
    );
  }
};

const shareOnFacebook = () => {
  try {
    const url = getShareableURL();
    const facebookURL = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url
    )}&quote=${encodeURIComponent(getShareMessage())}`;

    // Try to open popup with specific dimensions for Facebook
    const popup = window.open(
      facebookURL,
      "facebook-share",
      "width=580,height=296,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no"
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      // Popup blocked, fallback to new tab
      showNotification(
        "📘 Opening Facebook in new tab (popup blocked)",
        "info"
      );
      window.open(facebookURL, "_blank", "noopener,noreferrer");
    } else {
      showNotification("📘 Opening Facebook share window...", "success");

      // Check if popup was closed quickly (popup blocker)
      setTimeout(() => {
        if (popup.closed) {
          showNotification(
            "📋 Popup was blocked - opened in new tab instead",
            "info"
          );
        }
      }, 1000);
    }
  } catch (error) {
    console.error("Facebook share error:", error);
    showNotification(
      "❌ Failed to share on Facebook. Please try again.",
      "error"
    );
  }
};

// Helper function to get shareable URL
const getShareableURL = () => {
  const currentURL = window.location.href;

  // If it's a local file, provide a helpful fallback
  if (
    currentURL.startsWith("file://") ||
    currentURL.includes("localhost") ||
    currentURL.includes("127.0.0.1")
  ) {
    // For local development, create a generic sharing message
    return "https://threejs.org/examples/#webgl_galaxy"; // Three.js galaxy example as fallback
  }

  // For hosted versions, use the actual URL
  return currentURL;
};

// Enhanced share functions with better messaging
const getShareMessage = () => {
  const currentURL = window.location.href;

  if (
    currentURL.startsWith("file://") ||
    currentURL.includes("localhost") ||
    currentURL.includes("127.0.0.1")
  ) {
    return "Just created an amazing 3D Galaxy with planets, music, and cosmic animations! 🌌✨ Check out this epic space visualization!";
  } else {
    return "Check out this amazing 3D Galaxy Generator I found! 🌌✨ Interactive space exploration with planets and cosmic music!";
  }
};

// Notification system
const showNotification = (message, type = "info") => {
  // Remove existing notification
  const existing = document.querySelector(".notification");
  if (existing) {
    existing.remove();
  }

  // Create notification element
  const notification = document.createElement("div");
  notification.className = `notification notification-${type}`;
  notification.textContent = message;

  // Style the notification
  Object.assign(notification.style, {
    position: "fixed",
    top: "20px",
    right: "20px",
    padding: "12px 16px",
    borderRadius: "8px",
    color: "white",
    fontWeight: "500",
    fontSize: "14px",
    zIndex: "10000",
    transform: "translateX(400px)",
    transition: "transform 0.3s ease",
    maxWidth: "300px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
  });

  // Set background color based on type
  const colors = {
    success: "#10B981",
    error: "#EF4444",
    info: "#3B82F6",
    warning: "#F59E0B",
  };
  notification.style.backgroundColor = colors[type] || colors.info;

  // Add to DOM
  document.body.appendChild(notification);

  // Animate in
  setTimeout(() => {
    notification.style.transform = "translateX(0)";
  }, 100);

  // Remove after delay
  setTimeout(() => {
    notification.style.transform = "translateX(400px)";
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 300);
  }, 3000);
};

const copyLinkToClipboard = () => {
  try {
    const url = getShareableURL();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Modern clipboard API
      navigator.clipboard
        .writeText(url)
        .then(() => {
          showNotification("🔗 Link copied to clipboard!", "success");

          // Visual feedback on button
          const originalText = copyLink.textContent;
          copyLink.textContent = "Copied!";
          copyLink.style.backgroundColor = "rgba(16, 185, 129, 0.3)";

          setTimeout(() => {
            copyLink.textContent = originalText;
            copyLink.style.backgroundColor = "";
          }, 2000);
        })
        .catch((err) => {
          console.error("Clipboard write failed:", err);
          fallbackCopyToClipboard(url);
        });
    } else {
      // Fallback for older browsers
      fallbackCopyToClipboard(url);
    }
  } catch (error) {
    console.error("Copy link error:", error);
    showNotification("❌ Failed to copy link", "error");
  }
};

// Fallback copy function for older browsers
const fallbackCopyToClipboard = (text) => {
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);

    if (successful) {
      showNotification("🔗 Link copied to clipboard!", "success");

      // Visual feedback on button
      const originalText = copyLink.textContent;
      copyLink.textContent = "Copied!";
      copyLink.style.backgroundColor = "rgba(16, 185, 129, 0.3)";

      setTimeout(() => {
        copyLink.textContent = originalText;
        copyLink.style.backgroundColor = "";
      }, 2000);
    } else {
      showNotification(
        "❌ Copy failed. Please copy manually: " + text,
        "error"
      );
    }
  } catch (err) {
    console.error("Fallback copy failed:", err);
    showNotification("❌ Copy not supported. URL: " + text, "warning");
  }
};

// Tutorial functions
const showTutorial = () => {
  tutorialModal.classList.remove("hidden");
  tutorialVisible = true;
  currentTutorialStep = 1;
  updateTutorialStep();
};

const hideTutorial = () => {
  tutorialModal.classList.add("hidden");
  tutorialVisible = false;
  localStorage.setItem("galaxyTutorialSeen", "true");
};

const updateTutorialStep = () => {
  // Hide all steps
  document.querySelectorAll(".tutorial-step").forEach((step) => {
    step.classList.remove("active");
  });

  // Show current step
  const currentStep = document.querySelector(
    `[data-step="${currentTutorialStep}"]`
  );
  if (currentStep) {
    currentStep.classList.add("active");
  }

  // Update dots
  document.querySelectorAll(".dot").forEach((dot, index) => {
    dot.classList.toggle("active", index + 1 === currentTutorialStep);
  });

  // Update navigation buttons
  tutorialPrev.disabled = currentTutorialStep === 1;
  tutorialNext.disabled = currentTutorialStep === 4;

  if (currentTutorialStep === 4) {
    tutorialNext.textContent = "Finish";
  } else {
    tutorialNext.textContent = "Next";
  }
};

const nextTutorialStep = () => {
  if (currentTutorialStep < 4) {
    currentTutorialStep++;
    updateTutorialStep();
  } else {
    hideTutorial();
  }
};

const prevTutorialStep = () => {
  if (currentTutorialStep > 1) {
    currentTutorialStep--;
    updateTutorialStep();
  }
};

// Event listeners
if (infoToggle) infoToggle.addEventListener("click", toggleInfo);
if (settingsToggle) settingsToggle.addEventListener("click", toggleSettings);
if (musicToggle) musicToggle.addEventListener("click", toggleMusic);
if (fullscreenToggle)
  fullscreenToggle.addEventListener("click", toggleFullscreen);
if (resetView) resetView.addEventListener("click", resetCameraView);
if (screenshot) screenshot.addEventListener("click", takeScreenshot);
if (shareTwitter) shareTwitter.addEventListener("click", shareOnTwitter);
if (shareFacebook) shareFacebook.addEventListener("click", shareOnFacebook);
if (copyLink) copyLink.addEventListener("click", copyLinkToClipboard);
if (tutorialClose) tutorialClose.addEventListener("click", hideTutorial);
if (tutorialPrev) tutorialPrev.addEventListener("click", prevTutorialStep);
if (tutorialNext) tutorialNext.addEventListener("click", nextTutorialStep);
if (tutorialSkip) tutorialSkip.addEventListener("click", hideTutorial);

// Dot navigation for tutorial
document.querySelectorAll(".dot").forEach((dot, index) => {
  dot.addEventListener("click", () => {
    currentTutorialStep = index + 1;
    updateTutorialStep();
  });
});

// Keyboard shortcuts
document.addEventListener("keydown", (event) => {
  switch (event.key) {
    case "h":
    case "H":
      toggleInfo();
      break;
    case "g":
    case "G":
      toggleSettings();
      break;
    case "m":
    case "M":
      toggleMusic();
      break;
    case "f":
    case "F":
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        toggleFullscreen();
      }
      break;
    case "r":
    case "R":
      resetCameraView();
      break;
    case "s":
    case "S":
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        takeScreenshot();
      }
      break;
    case "Escape":
      if (tutorialVisible) {
        hideTutorial();
      }
      break;
  }
});

// Update stats when parameters change
const updateStatsWrapper = () => {
  updateStats();
};

// Generate initial galaxy and planets
generateGalaxy();
generatePlanets();

gui
  .add(paramaters, "count")
  .min(100)
  .max(600000)
  .step(100)
  .onFinishChange(() => {
    generateGalaxy();
    updateStats();
  });
gui
  .add(paramaters, "size")
  .min(0.001)
  .max(0.1)
  .step(0.001)
  .onFinishChange(() => {
    generateGalaxy();
    updateStats();
  });
gui
  .add(paramaters, "radius")
  .min(0.01)
  .max(20)
  .step(0.01)
  .onFinishChange(() => {
    generateGalaxy();
    updateStats();
  });
gui
  .add(paramaters, "branches")
  .min(2)
  .max(20)
  .step(1)
  .onFinishChange(() => {
    generateGalaxy();
    updateStats();
  });
gui
  .add(paramaters, "spin")
  .min(-5)
  .max(5)
  .step(0.001)
  .onFinishChange(() => {
    generateGalaxy();
    updateStats();
  });
gui
  .add(paramaters, "randomness")
  .min(0)
  .max(2)
  .step(0.001)
  .onFinishChange(() => {
    generateGalaxy();
    updateStats();
  });
gui
  .add(paramaters, "randomnessPower")
  .min(1)
  .max(10)
  .step(0.001)
  .onFinishChange(() => {
    generateGalaxy();
    updateStats();
  });
gui.addColor(paramaters, "insideColor").onFinishChange(() => {
  generateGalaxy();
  updateStats();
});
gui.addColor(paramaters, "outsideColor").onFinishChange(() => {
  generateGalaxy();
  updateStats();
});

// Planet controls
const planetFolder = gui.addFolder("Planets");
planetFolder
  .add(paramaters, "planetCount")
  .min(0)
  .max(50)
  .step(1)
  .onFinishChange(() => {
    generatePlanets();
    updateStats();
  });
planetFolder
  .add(paramaters, "planetMinSize")
  .min(0.01)
  .max(0.5)
  .step(0.01)
  .onFinishChange(() => {
    generatePlanets();
    updateStats();
  });
planetFolder
  .add(paramaters, "planetMaxSize")
  .min(0.1)
  .max(1.0)
  .step(0.01)
  .onFinishChange(() => {
    generatePlanets();
    updateStats();
  });

// Music controls
const musicFolder = gui.addFolder("Music");
musicFolder.add({ play: playAmbientMusic }, "play").name("Play Music");
musicFolder.add({ stop: stopAmbientMusic }, "stop").name("Stop Music");
musicFolder
  .add({ volume: 0.3 }, "volume", 0, 1)
  .onChange((value) => {
    if (gainNode) gainNode.gain.value = value;
  })
  .name("Volume");

// Initialize loading screen
simulateLoading();

// Apply initial mobile responsive settings
handleGUIResponsive();

// Initial stats update
updateStats();
updateMusicStatus();

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  // Handle GUI visibility on resize
  handleGUIResponsive();
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.x = 6;
camera.position.y = 4;
camera.position.z = 6;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  preserveDrawingBuffer: true, // Required for screenshots
  antialias: true, // Better visual quality
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
  const elapsedTime = clock.getElapsedTime();

  // Animate galaxy rotation
  if (points) {
    points.rotation.y = elapsedTime * 0.1; // Slow rotation around Y axis

    // Update shader uniforms for pulsing effect
    if (material && material.uniforms) {
      material.uniforms.uTime.value = elapsedTime;
    }
  }

  // Animate planets
  planets.forEach((planet) => {
    // Orbital motion
    planet.angle += planet.speed * 0.01;
    planet.mesh.position.x = Math.cos(planet.angle) * planet.distance;
    planet.mesh.position.z = Math.sin(planet.angle) * planet.distance;

    // Update atmosphere position
    if (planet.atmosphere) {
      planet.atmosphere.position.copy(planet.mesh.position);
      // Pulsing atmosphere effect
      const pulsing = 1 + Math.sin(elapsedTime * 2 + planet.angle) * 0.1;
      planet.atmosphere.scale.setScalar(pulsing);
      planet.atmosphereMaterial.opacity =
        0.05 + Math.sin(elapsedTime + planet.angle) * 0.05;
    }

    // Planet rotation
    planet.mesh.rotation.y += planet.rotationSpeed;

    // Add slight bobbing motion
    planet.mesh.position.y = Math.sin(elapsedTime * 0.5 + planet.angle) * 0.2;
  });

  // Animate lights for dynamic effect
  pointLight1.position.x = Math.cos(elapsedTime * 0.3) * 3;
  pointLight1.position.z = Math.sin(elapsedTime * 0.3) * 3;

  pointLight2.position.x = Math.cos(elapsedTime * 0.2 + Math.PI) * 4;
  pointLight2.position.z = Math.sin(elapsedTime * 0.2 + Math.PI) * 4;

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
